/**
 * Service Enrollments — READ-ONLY
 * Real schema: enrollments has NO status, NO progress_percent
 * Completion = course_progress.completed_at IS NOT NULL
 * Progress = course_progress.progress_percent
 *
 * Strategy: separate queries + JS joins (no nested PostgREST selects)
 * to avoid RLS cascading issues between enrollments ↔ course_progress.
 */
import { supabase } from '../config/supabase';
import { EnrollmentWithProgress, Course } from '../types';

const toCourse = (row: Record<string, unknown>): Course => ({
  id: row.id as string,
  organizationId: row.organization_id as string,
  title: row.title as string,
  description: row.description as string | undefined,
  thumbnailUrl: row.thumbnail_url as string | undefined,
  estimatedDurationMinutes: row.estimated_duration_minutes as number | undefined,
  isPublished: (row.is_published as boolean) ?? true,
  certificateEnabled: (row.certificate_enabled as boolean) ?? false,
  prerequisiteCourseId: row.prerequisite_course_id as string | undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date((row.updated_at as string) || (row.created_at as string)),
});

export const enrollmentsService = {
  /**
   * Get enrollments for a learner with progress and course details.
   * Uses 3 separate queries + JS joins to avoid nested PostgREST + RLS cascade.
   */
  async getByLearner(learnerId: string): Promise<EnrollmentWithProgress[]> {
    // Step 1: Fetch enrollments for this learner
    const { data: enrollments, error: enrollError } = await supabase
      .from('enrollments')
      .select('id, learner_id, course_id, due_date, enrolled_at')
      .eq('learner_id', learnerId)
      .order('enrolled_at', { ascending: false });

    if (enrollError) {
      console.error('Error fetching enrollments:', enrollError);
      throw new Error('Impossible de charger les inscriptions');
    }

    if (!enrollments || enrollments.length === 0) return [];

    const enrollmentIds = enrollments.map(e => e.id);
    const courseIds = [...new Set(enrollments.map(e => e.course_id))];

    // Step 2: Parallel fetch of course_progress and courses
    const [progressResult, coursesResult] = await Promise.all([
      supabase
        .from('course_progress')
        .select('enrollment_id, progress_percent, completed_at')
        .in('enrollment_id', enrollmentIds),
      supabase
        .from('courses')
        .select('*')
        .in('id', courseIds),
    ]);

    if (progressResult.error) {
      console.error('Error fetching course_progress:', progressResult.error);
    }
    if (coursesResult.error) {
      console.error('Error fetching courses:', coursesResult.error);
    }

    // Build maps
    const progressMap = new Map<string, { progressPercent: number; completedAt?: Date }>();
    (progressResult.data || []).forEach((cp) => {
      progressMap.set(cp.enrollment_id, {
        progressPercent: cp.progress_percent || 0,
        completedAt: cp.completed_at ? new Date(cp.completed_at) : undefined,
      });
    });

    const coursesMap = new Map<string, Course>();
    (coursesResult.data || []).forEach((c) => {
      coursesMap.set(c.id, toCourse(c as Record<string, unknown>));
    });

    // Step 3: Build result
    return enrollments.map((row) => {
      const cp = progressMap.get(row.id);
      const progressPercent = cp?.progressPercent || 0;
      const completedAt = cp?.completedAt;

      return {
        id: row.id,
        learnerId: row.learner_id,
        courseId: row.course_id,
        dueDate: row.due_date ? new Date(row.due_date) : undefined,
        enrolledAt: new Date(row.enrolled_at),
        createdAt: new Date(row.enrolled_at),
        course: coursesMap.get(row.course_id),
        courseProgress: cp ? {
          id: '',
          enrollmentId: row.id,
          progressPercent,
          completedAt,
          createdAt: new Date(),
          updatedAt: new Date(),
        } : undefined,
        progressPercent,
        isCompleted: !!completedAt,
        completedAt,
      };
    });
  },

  /**
   * Get organization enrollment stats via RPC (accurate, no N+1)
   */
  async getOrganizationStats(organizationId: string | null): Promise<{
    totalEnrollments: number;
    completedEnrollments: number;
    inProgressEnrollments: number;
    notStartedEnrollments: number;
  }> {
    const { data, error } = await supabase.rpc('get_enrollment_status_distribution', {
      p_org_id: organizationId || null,
    });

    if (error) {
      console.error('Error fetching enrollment stats:', error);
      return {
        totalEnrollments: 0,
        completedEnrollments: 0,
        inProgressEnrollments: 0,
        notStartedEnrollments: 0,
      };
    }

    const stats = data || [];
    let total = 0;
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    stats.forEach((row: { status: string; count: number }) => {
      const count = Number(row.count) || 0;
      total += count;
      if (row.status === 'completed') completed = count;
      else if (row.status === 'in_progress') inProgress = count;
      else if (row.status === 'not_started') notStarted = count;
    });

    return {
      totalEnrollments: total,
      completedEnrollments: completed,
      inProgressEnrollments: inProgress,
      notStartedEnrollments: notStarted,
    };
  },

  /**
   * Get monthly enrollment stats via RPC
   */
  async getMonthlyStats(organizationId: string | null, months: number = 7) {
    const { data, error } = await supabase.rpc('get_monthly_enrollment_stats', {
      p_org_id: organizationId,
      p_months: months,
    });

    if (error) {
      console.error('Error fetching monthly stats:', error);
      return [];
    }

    return (data || []).map((row: { month_key: string; inscriptions: number; completions: number }) => ({
      monthKey: row.month_key,
      inscriptions: Number(row.inscriptions) || 0,
      completions: Number(row.completions) || 0,
    }));
  },

  /**
   * Get top courses by enrollment count via RPC
   */
  async getTopCourses(organizationId: string | null, limit: number = 5) {
    const { data, error } = await supabase.rpc('get_top_courses_by_enrollments', {
      p_org_id: organizationId,
      p_limit: limit,
    });

    if (error) {
      console.error('Error fetching top courses:', error);
      return [];
    }

    return (data || []).map((row: { course_name: string; inscrits: number }) => ({
      courseName: row.course_name,
      inscrits: Number(row.inscrits) || 0,
    }));
  },

  /**
   * Get group completion rates via RPC
   */
  async getGroupCompletionRates(organizationId: string | null) {
    const { data, error } = await supabase.rpc('get_group_completion_rates', {
      p_org_id: organizationId,
    });

    if (error) {
      console.error('Error fetching group completion rates:', error);
      return [];
    }

    return (data || []).map((row: { group_name: string; total_enrollments: number; completed_enrollments: number; taux: number }) => ({
      groupName: row.group_name,
      totalEnrollments: Number(row.total_enrollments) || 0,
      completedEnrollments: Number(row.completed_enrollments) || 0,
      taux: Number(row.taux) || 0,
    }));
  },
};
